import React, { createContext, useContext, useState, ReactNode } from "react";
import {
  createTheme,
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material";

export type ThemeContextType = {
  isDarkTheme: boolean;
  toggleTheme: () => void;
};

type ThemeProviderProps = {
  children: ReactNode;
};

const initialTheme = createTheme({
  palette: {
    primary: {
      main: "#516daf",
      contrastText: "#1f1f1f",
    },
    secondary: {
      main: "#274cd1",
      contrastText: "#1f1f1f",
    },
  },
});

const ThemeContext = createContext<ThemeContextType>({
  isDarkTheme: true,
  toggleTheme: () => {},
});

const GlobalStyles = () => <CssBaseline />;

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [globalTheme, setTheme] = useState(initialTheme);

  const getComputedCSSVariable = (variableName: string) => {
    if (typeof window === "undefined") {
      return "#ffffff"; // Fallback seguro para SSR
    }

    const computedValue = getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();

    if (!computedValue) {
      console.warn(`A variável CSS ${variableName} não está definida.`);
      return "#ffffff";
    }

    return computedValue;
  };

  const toggleTheme = () => {
    setIsDarkTheme((prevIsDarkTheme) => !prevIsDarkTheme);

    const theme = createTheme({
      palette: {
        primary: {
          main: getComputedCSSVariable("--cor-primaria"),
          contrastText: getComputedCSSVariable("--cor-texto-primaria"),
        },
        secondary: {
          main: getComputedCSSVariable("--cor-secundaria"),
          contrastText: getComputedCSSVariable("--cor-texto-secundaria"),
        },
        background: {
          default: getComputedCSSVariable("--cor-fundo"),
          paper: getComputedCSSVariable("--cor-papel"),
        },
        text: {
          primary: getComputedCSSVariable("--cor-texto"),
          secondary: getComputedCSSVariable("--cor-texto-secundaria"),
        },
        error: {
          main: getComputedCSSVariable("--cor-erro"),
        },
        warning: {
          main: getComputedCSSVariable("--cor-alerta"),
        },
        success: {
          main: getComputedCSSVariable("--cor-sucesso"),
        },
      },
    });

    setTheme(theme);
  };


  return (
    <ThemeContext.Provider value={{ isDarkTheme, toggleTheme }}>
      <MuiThemeProvider theme={globalTheme}>
        <GlobalStyles />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );

  /*
  return (
    <ThemeContext.Provider value={{ isDarkTheme, toggleTheme }}>
      <MuiThemeProvider theme={globalTheme}>
        <GlobalStyles />
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
  */

};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  return context;
};
